import { NodeItem, NodeType } from '@/views/flow/nodeview/NodeItem';
import { CIcon } from './CIcon';
import { CText } from './CText';
import toast from 'react-hot-toast';
import { useEffect } from 'react';
import { Model3DType } from '@/function/T3dEngieer';
import { useTheme } from 'next-themes';

export const NodePlaceholderConstant = {
  width: 149,
  height: 294,
};

export function NodePlaceholder(props: {
  srcNode: NodeItem | undefined;
  allNode: NodeItem[];
  onSelect: (type: string) => void;
  targetNode?: NodeItem | undefined;
}) {
  const btnList = [
    { text: 'Prompt', icon: 'text', value: NodeType.PROMPT },
    { text: 'Images', icon: 'image', value: NodeType.IMAGE },
    { text: 'Sketch', icon: 'brush', value: NodeType.DRAWING },
    { text: 'Palette', icon: 'grid', value: NodeType.PALETTE },
    { text: 'Video', icon: 'video', value: NodeType.VIDEO },
    { text: '3D', icon: '3d', value: NodeType.THREE_D },
  ];

  useEffect(() => {
    if (props.srcNode && props.srcNode.type === NodeType.PALETTE) {
      // palette 속성 미선택시 연결 불가
      if (!props.srcNode.text) {
        toast.error('Select palette type.');
      }
    }
  }, []);

  return (
    <div
      className="bg-[#FBFBFB] dark:bg-[#303030] border-[0.5px] border-solid border-[#000000]/10 dark:border-[#393A3E] rounded-[20px] gap-[8px] p-[12px] flex flex-col items-center
      justify-center shadow-[0px_0px_30px_0px_rgba(0,0,0,0.18)] dark:shadow-[0px_0px_30px_0px_rgba(0,0,0,0.6)]"
      style={{
        width: NodePlaceholderConstant.width,
        height: NodePlaceholderConstant.height,
      }}
    >
      {btnList.map((item, index) => (
        <IconButton
          key={index}
          srcNode={props.srcNode}
          allNode={props.allNode}
          text={item.text}
          icon={item.icon}
          value={item.value}
          onSelect={() => props.onSelect(item.value)}
          targetNode={props.targetNode}
        />
      ))}
    </div>
  );
}

function IconButton(props: {
  srcNode: NodeItem | undefined;
  allNode: NodeItem[];
  text: string;
  icon: string;
  value: string;
  onSelect: () => void;
  targetNode?: NodeItem | undefined;
}) {
  const { resolvedTheme } = useTheme();

  const sourceNode = props.srcNode ? props.srcNode : new NodeItem().setType(props.value);
  const targetNode = props.targetNode ? props.targetNode : new NodeItem().setType(props.value);
  let isDisabled = !sourceNode.canConnect(targetNode);

  if (!isDisabled) {
    // handle source node
    if (sourceNode.type === NodeType.PALETTE) {
      // palette 속성 미선택시 연결 불가
      if (!sourceNode.text) {
        isDisabled = true;
      } else if (targetNode.type === NodeType.IMAGE || targetNode.type === NodeType.PROMPT) {
        // palette - image / palette - prompt는 한 개만 연결 가능
        const connectImage = props.allNode.filter((n) => {
          return n.type === NodeType.IMAGE && n.source.includes(sourceNode.id);
        });
        const connectPrompt = props.allNode.filter((n) => {
          return n.type === NodeType.PROMPT && n.source.includes(sourceNode.id);
        });
        if (connectImage.length > 0 || connectPrompt.length > 0) isDisabled = true;
      }
    } else if (sourceNode.type === NodeType.IMAGE) {
      // image에서 바로 palette 생성 불가
      if (targetNode.type === NodeType.PALETTE) isDisabled = true;
    } else if (sourceNode.type === NodeType.DRAWING) {
      // drawing에 image는 하나만
      const connectImage = props.allNode.filter((n) => {
        return n.type === NodeType.IMAGE && n.source.includes(sourceNode.id);
      });
      if (connectImage.length > 0) isDisabled = true;
    }

    // handle target node
    if (targetNode.type === NodeType.IMAGE) {
      // drawing과 연결된 image는 하나의 drawing, prompt만 가질 수 있음
      const connectDrawing = props.allNode.filter((n) => {
        return n.type === NodeType.DRAWING && n.target.includes(targetNode.id);
      });
      if (connectDrawing.length > 0) {
        if (sourceNode.type === NodeType.PROMPT) {
          const connectPrompt = props.allNode.filter(
            (n) => n.type === NodeType.PROMPT && n.target.includes(targetNode.id),
          );
          if (connectPrompt.length > 0) isDisabled = true;
        } else isDisabled = true;
      }
    } else if (targetNode.type === NodeType.THREE_D) {
      // handle t3d node
      if (sourceNode.type === NodeType.PROMPT) {
        // handle prompt to three d node
        if (targetNode.model === Model3DType.HUNYUAN3D_TURBO) isDisabled = true;

        // prompt 한 개 제한
        const connectPrompt = props.allNode.filter((n) => {
          return n.type === NodeType.PROMPT && n.target.includes(targetNode.id);
        });
        if (connectPrompt.length > 0) isDisabled = true;
      } else if (sourceNode.type === NodeType.IMAGE) {
        // image 한 개 제한
        const connectImage = props.allNode.filter((n) => {
          return n.type === NodeType.IMAGE && n.target.includes(targetNode.id);
        });
        if (connectImage.length > 0) isDisabled = true;
      }
    }
  }

  return (
    <button
      className={`flex w-full items-center justify-start rounded-[7.5px] duration-300 gap-[12px] pr-[20px] 
            ${
              isDisabled
                ? ''
                : 'cursor-pointer hover:bg-[#000000]/10 dark:hover:bg-[#303030] dark:hover:bg-gradient-to-r dark:hover:from-[#ff3496]/30 dark:hover:to-[#ff7700]/30'
            }`}
      type="button"
      disabled={isDisabled}
      onClick={() => {
        props.onSelect();
      }}
    >
      <div className="w-[36px] h-[36px] flex items-center justify-center">
        <CIcon
          name={props.icon}
          size={props.icon === 'grid' ? 22 : 27}
          color={isDisabled ? '#888' : resolvedTheme === 'dark' ? 'white' : '#404040'}
        />
      </div>
      <CText
        type="body1"
        color={isDisabled ? '#888' : resolvedTheme === 'dark' ? 'white' : '#404040'}
      >
        {props.text}
      </CText>
    </button>
  );
}
